import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, Store, Zap, UserPlus, TrendingDown, Hourglass, Rocket,
  XCircle, Clock, CreditCard, Users, Crown, BarChart3, MailCheck
} from 'lucide-react';
import { adminMetricsApi, type GrowthMetrics } from '../../api/adminMetricsApi';
import { adminCampaignsApi } from '../../api/adminCampaignsApi';
import ActionCard from './ActionCard';
import CountUp from '../ui/CountUp';

const fmtMoney = (n: number | undefined): string =>
  typeof n === 'number' && !isNaN(n)
    ? `$${new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(n)}`
    : '—';

const fmtNum = (n: number | undefined): string =>
  typeof n === 'number' && !isNaN(n) ? new Intl.NumberFormat('es-MX').format(n) : '—';

interface AdminHomePanelProps {
  expiredCount: number;
  expiringSoonCount: number;
  pendingPayments: number;
}

// Home de admin: caja registradora (KPIs del negocio Lealify) + radar de
// churn/venta. Todo sale de adminMetricsApi.growth (mismo backend que el Panel de
// Crecimiento) + las alertas que el dashboard ya consultaba.
const AdminHomePanel: React.FC<AdminHomePanelProps> = ({ expiredCount, expiringSoonCount, pendingPayments }) => {
  const navigate = useNavigate();
  const [growth, setGrowth] = useState<GrowthMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [emailReqCount, setEmailReqCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    adminMetricsApi.growth()
      .then(d => { if (!cancelled) setGrowth(d); })
      .catch(err => console.error('[AdminHome] growth metrics:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    // Solicitudes de correo pendientes de aprobar (badge accionable).
    adminCampaignsApi.listPendingEmail()
      .then(rows => { if (!cancelled) setEmailReqCount(rows.length); })
      .catch(() => { /* silencioso: no rompe el home si falla */ });
    return () => { cancelled = true; };
  }, []);

  const revenue = growth?.revenue;
  const altasMes = growth?.altas?.length ? growth.altas[growth.altas.length - 1].n : null;

  // Radar: solo lo accionable
  const proEnRiesgo = revenue?.proEnRiesgo || [];
  const pruebasPorVencer = (revenue?.enPrueba || []).filter(p => p.venceDias != null && p.venceDias <= 7);
  const candidatos = growth?.candidatosPro || [];

  const cards: React.ReactNode[] = [];

  if (emailReqCount > 0) {
    cards.push(
      <ActionCard
        key="email-req"
        tone="amber"
        icon={<MailCheck className="h-5 w-5" />}
        title={emailReqCount === 1 ? '1 solicitud de campaña de correo' : `${emailReqCount} solicitudes de campaña de correo`}
        sub="Un negocio pidió enviar una campaña por correo — está en revisión y no sale hasta que la apruebes."
        cta="Revisar"
        to="/dashboard/admin/emails?tab=solicitudes"
      />
    );
  }

  if (proEnRiesgo.length > 0) {
    const top = proEnRiesgo[0];
    const uso = top.ultVisita == null ? 'nunca ha usado el kiosco' : `${top.ultVisita} días sin usar`;
    cards.push(
      <ActionCard
        key="risk"
        tone="purple"
        icon={<TrendingDown className="h-5 w-5" />}
        title={proEnRiesgo.length === 1 ? '1 negocio PRO en riesgo de cancelar' : `${proEnRiesgo.length} negocios PRO en riesgo de cancelar`}
        sub={`${top.name} (${uso})${proEnRiesgo.length > 1 ? ` y ${proEnRiesgo.length - 1} más` : ''} — pagan pero no entran; una llamada salva la renovación.`}
        cta="Ver lista"
        to="/dashboard#crecimiento"
      />
    );
  }

  if (pruebasPorVencer.length > 0) {
    const top = pruebasPorVencer[0];
    cards.push(
      <ActionCard
        key="trial"
        tone="blue"
        icon={<Hourglass className="h-5 w-5" />}
        title={pruebasPorVencer.length === 1 ? '1 prueba vence esta semana' : `${pruebasPorVencer.length} pruebas vencen esta semana`}
        sub={`${top.name} — ${top.venceDias === 0 ? 'vence hoy' : `${top.venceDias} día${top.venceDias === 1 ? '' : 's'} restante${top.venceDias === 1 ? '' : 's'}`}${pruebasPorVencer.length > 1 ? ` y ${pruebasPorVencer.length - 1} más` : ''} — ciérralo antes del corte.`}
        cta="Ver pruebas"
        // Lleva a la lista de suscripciones acotada a PRUEBAS por vencer (`trial=1`), no al panel
        // de analíticas: la tarjeta habla de quién vence, así que el botón debe enseñar justo eso.
        to="/dashboard/admin/subscriptions?plan=pro&expirationStatus=expiring-soon&trial=1"
      />
    );
  }

  if (candidatos.length > 0) {
    const top = candidatos[0];
    cards.push(
      <ActionCard
        key="upsell"
        tone="green"
        icon={<Rocket className="h-5 w-5" />}
        title={candidatos.length === 1 ? '1 negocio free listo para PRO' : `${candidatos.length} negocios free listos para PRO`}
        sub={`${top.name} (${fmtNum(top.vis)} visitas, ${fmtNum(top.cli)} clientes)${candidatos.length > 1 ? ` y ${candidatos.length - 1} más` : ''} — venta caliente.`}
        cta="Ver candidatos"
        to="/dashboard#crecimiento"
      />
    );
  }

  if (expiredCount > 0) {
    cards.push(
      <ActionCard
        key="expired"
        tone="red"
        icon={<XCircle className="h-5 w-5" />}
        title={expiredCount === 1 ? '1 suscripción PRO vencida' : `${expiredCount} suscripciones PRO vencidas`}
        sub="Sin renovar — revisa si es impago o cancelación."
        cta="Revisar"
        to="/dashboard/admin/subscriptions?plan=pro&expirationStatus=expired"
      />
    );
  }

  if (expiringSoonCount > 0) {
    cards.push(
      <ActionCard
        key="expiring"
        tone="orange"
        icon={<Clock className="h-5 w-5" />}
        title={expiringSoonCount === 1 ? '1 suscripción vence en los próximos 7 días' : `${expiringSoonCount} suscripciones vencen en los próximos 7 días`}
        sub="Confirma la renovación antes del corte."
        cta="Revisar"
        to="/dashboard/admin/subscriptions?plan=pro&expirationStatus=expiring-soon"
      />
    );
  }

  if (pendingPayments > 0) {
    cards.push(
      <ActionCard
        key="payments"
        tone="yellow"
        icon={<CreditCard className="h-5 w-5" />}
        title={pendingPayments === 1 ? '1 pago esperando aprobación' : `${pendingPayments} pagos esperando aprobación`}
        sub="Transferencias por validar."
        cta="Aprobar"
        to="/dashboard/admin/payments"
      />
    );
  }

  const quick = [
    { icon: Users, label: 'Negocios', sub: growth ? `${fmtNum(growth.total)} registrados` : 'buscar, ficha, entrar como', to: '/dashboard/admin/users' },
    { icon: Crown, label: 'Suscripciones', sub: 'planes y vencimientos', to: '/dashboard/admin/subscriptions' },
    { icon: CreditCard, label: 'Pagos', sub: 'historial y pendientes', to: '/dashboard/admin/payments' },
    { icon: BarChart3, label: 'Crecimiento', sub: 'detalle, aquí abajo', to: '/dashboard#crecimiento' },
  ];

  return (
    <div className="space-y-6">
      {/* KPIs del negocio Lealify */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-content-3 mb-2">El negocio hoy</h3>
        {loading && !growth ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map(i => <div key={i} className="h-24 bg-skeleton rounded-lg animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <button type="button" onClick={() => navigate('/dashboard#crecimiento')} className="text-left bg-surface border border-line rounded-lg shadow-sm p-4 hover:border-accent transition-colors">
              <p className="text-xs text-content-3 flex items-center"><DollarSign className="h-3.5 w-3.5 mr-1" /> MRR</p>
              <p className="text-2xl font-display font-extrabold text-green-600 dark:text-green-400 mt-1"><CountUp value={revenue?.mrr ?? 0} format={fmtMoney} /></p>
              <p className="text-xs text-content-3 mt-0.5">ARR {fmtMoney(revenue?.arr)} MXN</p>
            </button>
            <button type="button" onClick={() => navigate('/dashboard/admin/subscriptions')} className="text-left bg-surface border border-line rounded-lg shadow-sm p-4 hover:border-accent transition-colors">
              <p className="text-xs text-content-3 flex items-center"><Store className="h-3.5 w-3.5 mr-1" /> Negocios pagando</p>
              <p className="text-2xl font-display font-extrabold text-content mt-1"><CountUp value={revenue?.pagando ?? 0} format={fmtNum} /></p>
              <p className="text-xs text-content-3 mt-0.5"><span className="font-semibold text-content-2">{revenue?.planPagando?.pro ?? '—'}</span> PRO · <span className="font-semibold text-content-2">{revenue?.planPagando?.premium ?? '—'}</span> Premium</p>
            </button>
            <button type="button" onClick={() => navigate('/dashboard#crecimiento')} className="text-left bg-surface border border-line rounded-lg shadow-sm p-4 hover:border-accent transition-colors">
              <p className="text-xs text-content-3 flex items-center"><Zap className="h-3.5 w-3.5 mr-1" /> Activos ahora</p>
              <p className="text-2xl font-display font-extrabold text-content mt-1"><CountUp value={growth?.activosAhora ?? 0} format={fmtNum} /></p>
              <p className="text-xs text-content-3 mt-0.5">de {fmtNum(growth?.total)} registrados</p>
            </button>
            <button type="button" onClick={() => navigate('/dashboard#crecimiento')} className="text-left bg-surface border border-line rounded-lg shadow-sm p-4 hover:border-accent transition-colors">
              <p className="text-xs text-content-3 flex items-center"><UserPlus className="h-3.5 w-3.5 mr-1" /> Altas del mes</p>
              <p className="text-2xl font-display font-extrabold text-content mt-1">{altasMes == null ? '—' : <CountUp value={altasMes} format={fmtNum} />}</p>
              <p className="text-xs text-content-3 mt-0.5">conversión a PRO {growth ? `${growth.conversionPro}%` : '—'}</p>
            </button>
          </div>
        )}
      </div>

      {/* Radar: churn > cierre de venta > cobranza. Sin pendientes → limpio */}
      {cards.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-content-3 mb-2">Para hoy</h3>
          <div className="space-y-2">{cards}</div>
        </div>
      )}

      {/* Accesos rápidos */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-content-3 mb-2">Accesos rápidos</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {quick.map(a => {
            const Icon = a.icon;
            return (
              <button
                key={a.to}
                type="button"
                onClick={() => navigate(a.to)}
                className="text-left bg-surface border border-line rounded-lg shadow-sm p-4 flex items-center gap-3 hover:border-accent transition-colors"
              >
                <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-accent/10 text-accent flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-content truncate">{a.label}</span>
                  <span className="block text-[11px] text-content-3">{a.sub}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminHomePanel;
